/**
 * Phase 6E — Selective Entry Hub (student-portal).
 *
 * Renders only when the student's learning plan program matches one of:
 *   • Selective Entry
 *   • VCE Selective
 *   • Scholarship Preparation
 *
 * Beautiful hub linking the existing Learning Centre, Exam Centre, Resources,
 * Discussion Hub, AI Coach, Leaderboard and Achievements (all already built).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  GraduationCap, BookOpen, Trophy, FileText, MessageCircle, Sparkles,
  Brain, Award, Calendar, ArrowRight, Flame, Target, ChevronRight, Star,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const ELIGIBLE_PROGRAMS = ['selective entry', 'vce selective', 'scholarship preparation', 'scholarship'];

const isEligibleProgram = (p) => {
  if (!p) return false;
  const s = String(p).toLowerCase();
  return ELIGIBLE_PROGRAMS.some(k => s.includes(k));
};

const HubCard = ({ to, icon: Icon, title, sub, badge, testid, accent = false }) => (
  <Link to={to} data-testid={testid}
    className={`group relative rounded-2xl border p-5 transition-all hover:-translate-y-0.5 ${accent ? 'bg-black text-white border-[#D3D86F]/30 hover:border-[#D3D86F]' : 'bg-white text-gray-900 border-gray-200 hover:border-red-600 hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)]'}`}>
    {badge && (
      <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-[#D3D86F]/30 text-black border border-[#D3D86F]">
        <span className="w-1.5 h-1.5 rounded-full bg-red-700 animate-pulse" /> {badge}
      </span>
    )}
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${accent ? 'bg-[#D3D86F] text-black' : 'bg-red-50 text-red-700'}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div className="font-bold text-base">{title}</div>
    <div className={`text-[11px] mt-1 ${accent ? 'text-white/70' : 'text-gray-600'}`}>{sub}</div>
    <ArrowRight className={`h-4 w-4 absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity ${accent ? 'text-[#D3D86F]' : 'text-red-700'}`} />
  </Link>
);

const StatPill = ({ icon: Icon, value, label, testid, tone = 'amber' }) => {
  const tones = {
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    red: 'bg-rose-50 text-rose-800 border-rose-200',
    sky: 'bg-sky-50 text-sky-800 border-sky-200',
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] font-bold">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
};

const SelectiveEntryHub = ({ planProgram }) => {
  // Always evaluate hooks before any early return
  const [progress, setProgress] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [achievements, setAchievements] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [p, l, a] = await Promise.all([
          ax.get('/api/me/student/learning/progress').catch(() => ({ data: null })),
          ax.get('/api/exam/leaderboard?limit=5').catch(() => ({ data: { items: [] } })),
          ax.get('/api/me/student/exam/achievements').catch(() => ({ data: null })),
        ]);
        if (!live) return;
        setProgress(p.data || null);
        setLeaderboard(l.data?.items || []);
        setAchievements(a.data || null);
      } catch (_e) { /* silent */ }
    })();
    return () => { live = false; };
  }, []);

  // Gate after hooks
  if (!isEligibleProgram(planProgram)) return null;

  const streak = progress?.streak_days ?? achievements?.streak_days ?? 0;
  const xp = progress?.total_xp ?? achievements?.xp ?? 0;
  const level = progress?.level ?? Math.max(1, Math.floor((xp || 0) / 250) + 1);
  const topicsCompleted = progress?.topics_completed ?? 0;
  const mocksTaken = progress?.mocks_taken ?? achievements?.mocks_completed ?? 0;
  const weakest = (progress?.weakest_topics || []).slice(0, 3);
  const continueTopic = progress?.continue_topic || null;
  const myRank = progress?.leaderboard_rank ?? achievements?.rank ?? null;

  return (
    <section className="rounded-2xl border border-[#D3D86F]/40 bg-gradient-to-br from-black via-[#0a0a0a] to-black text-white p-6 shadow-lg overflow-hidden relative" data-testid="selective-entry-hub">
      <div className="absolute inset-0 opacity-50 pointer-events-none" style={{
        background: 'radial-gradient(60% 50% at 20% 0%, rgba(220,38,38,0.25) 0%, rgba(220,38,38,0) 60%), radial-gradient(50% 40% at 85% 100%, rgba(211,216,111,0.16) 0%, rgba(211,216,111,0) 70%)',
      }} />
      <div className="relative">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D3D86F]">Winhouse Selective Entry Learning Centre</div>
            <h2 className="text-2xl font-bold mt-1 flex items-center gap-2">
              <GraduationCap className="h-6 w-6 text-[#D3D86F]" />
              Your study hub
            </h2>
            <p className="text-white/65 text-sm mt-1">All topics · all mocks · all resources — together in one place.</p>
          </div>
          <Link to="/student/learning" className="h-10 px-5 rounded-full bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-bold text-xs uppercase tracking-wider inline-flex items-center gap-1.5" data-testid="seh-open-learning">
            Open Learning Centre <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5">
          <StatPill icon={Flame} value={`${streak}d`} label="Streak" testid="seh-stat-streak" tone="amber" />
          <StatPill icon={Sparkles} value={xp} label="XP" testid="seh-stat-xp" tone="sky" />
          <StatPill icon={Target} value={topicsCompleted} label="Topics done" testid="seh-stat-topics" tone="emerald" />
          <StatPill icon={Trophy} value={mocksTaken} label="Mocks taken" testid="seh-stat-mocks" tone="red" />
        </div>

        {/* Continue where you left off */}
        {continueTopic && (
          <Link to={`/student/learning?topic=${continueTopic.slug}`} className="block bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#D3D86F]/40 rounded-xl p-3 mb-4 transition-colors" data-testid="seh-continue">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#D3D86F] font-bold">Continue where you left off</div>
                <div className="font-bold text-sm mt-0.5 truncate">{continueTopic.title || continueTopic.name || continueTopic.slug}</div>
                {continueTopic.progress != null && (
                  <div className="mt-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-[#D3D86F]" style={{ width: `${Math.max(2, Math.min(100, continueTopic.progress || 0))}%` }} />
                  </div>
                )}
              </div>
              <ChevronRight className="h-5 w-5 text-[#D3D86F] shrink-0" />
            </div>
          </Link>
        )}

        {/* Hub cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <HubCard to="/student/learning" icon={BookOpen} title="Learning Centre" sub="Topics · lessons · quizzes" testid="seh-card-learning" accent />
          <HubCard to="/student/exams" icon={Trophy} title="Mock Exams" sub="90+ full-length papers" testid="seh-card-exams" />
          <HubCard to="/student/learning?tab=practice" icon={Brain} title="Practice Questions" sub="Adaptive · timed · feedback" testid="seh-card-practice" />
          <HubCard to="/resources" icon={FileText} title="Resources" sub="Formulas · vocab · packs" testid="seh-card-resources" />
          <HubCard to="/student/learning?tab=hub" icon={MessageCircle} title="Discussion Hub" sub="Chat with your class" testid="seh-card-hub" />
          <HubCard to="/student/learning?tab=coach" icon={Sparkles} title="AI Study Coach" sub="Ask · explain · drill" badge="Live" testid="seh-card-coach" />
          <HubCard to="/student/exams?tab=leaderboard" icon={Star} title="Leaderboard" sub={myRank ? `You: #${myRank}` : 'Weekly · monthly'} testid="seh-card-leaderboard" />
          <HubCard to="/student/exams?tab=achievements" icon={Award} title="Achievements" sub="Badges · certificates" testid="seh-card-achievements" />
        </div>

        {/* Weakest topics + leaderboard preview */}
        <div className="grid md:grid-cols-2 gap-3 mt-5">
          <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid="seh-weakest">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#D3D86F] font-bold flex items-center gap-1.5"><Target className="h-3 w-3" /> Recommended revision</div>
            {weakest.length === 0 ? (
              <p className="text-white/60 text-xs mt-2 italic">Complete a few topics to receive personalised revision recommendations.</p>
            ) : (
              <ul className="mt-2 space-y-1.5">
                {weakest.map(t => (
                  <li key={t.slug || t.title} className="flex items-center justify-between text-sm">
                    <Link to={`/student/learning?topic=${t.slug || ''}`} className="hover:text-[#D3D86F] truncate">{t.title || t.name}</Link>
                    {t.accuracy != null && <span className="text-[10px] text-white/50">{Math.round(t.accuracy * 100)}%</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid="seh-leaderboard">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#D3D86F] font-bold flex items-center gap-1.5"><Trophy className="h-3 w-3" /> Top of the leaderboard</div>
            {leaderboard.length === 0 ? (
              <p className="text-white/60 text-xs mt-2 italic">Leaderboard updates after the first mock attempt.</p>
            ) : (
              <ol className="mt-2 space-y-1.5 text-sm">
                {leaderboard.slice(0, 5).map((row, i) => (
                  <li key={row.username || i} className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${i === 0 ? 'bg-[#D3D86F] text-black' : 'bg-white/10 text-white/80'}`}>{i + 1}</span>
                      <span className="font-semibold">{row.username || 'Anonymous'}</span>
                    </span>
                    <span className="text-[#D3D86F] font-bold text-xs">{row.xp || row.score || row.points || 0} XP</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        {/* Smart reminders */}
        <div className="mt-5 flex flex-wrap gap-2 items-center text-xs text-white/70">
          <Calendar className="h-3.5 w-3.5 text-[#D3D86F]" />
          <span className="font-semibold text-white/85">Daily nudge:</span>
          <span>5 questions today keeps your streak alive 🔥</span>
        </div>
      </div>
    </section>
  );
};

export default SelectiveEntryHub;
